import PropTypes from 'prop-types';
import { lazy, Suspense } from 'react';
import { m } from 'framer-motion';
// @mui
import { Box, Container, Typography, Button, Link } from '@mui/material';
// hooks
import useAuth from '../hooks/useAuth';
// components
import { MotionContainer, varBounce } from '../components/animate';
// access logic (shared with the nav so sections/items stay in sync)
import checkAccess from './checkNavAccess';

// ----------------------------------------------------------------------

// This guard wraps every routed page, so it sits in the initial chunk. Pulling
// the illustration through the src/assets barrel brought the 403, 404 and 500
// artwork with it - three inline SVG modules, about 500 KB of the initial
// bundle - onto every page load, to draw a graphic almost nobody ever sees.
// Loaded here only when access is actually denied.
const ForbiddenIllustration = lazy(() => import('../assets/illustration_403'));

// ----------------------------------------------------------------------

RoleBasedGuard.propTypes = {
  // Legacy props — still fully supported, no breaking change
  roles: PropTypes.arrayOf(PropTypes.string),
  allowed: PropTypes.shape({
    org: PropTypes.arrayOf(PropTypes.string),
    user: PropTypes.arrayOf(PropTypes.string),
  }),
  slug: PropTypes.string,
  // New permission-code props (checked FIRST when present)
  permission: PropTypes.string,                        // single code — must have this
  permissions: PropTypes.arrayOf(PropTypes.string),    // any-of — must have at least one
  isNav: PropTypes.bool,
  children: PropTypes.node.isRequired,
};

export default function RoleBasedGuard({ roles, allowed, slug, permission, permissions, isNav, children }) {
  const { user, hasPermission } = useAuth();

  const hasAccess = checkAccess({ user, hasPermission, roles, allowed, slug, permission, permissions });

  if (hasAccess) {
    return <>{children}</>;
  }

  if (isNav) {
    return null;
  }

  return (
    <Container component={MotionContainer} sx={{ textAlign: 'center' }}>
      <m.div variants={varBounce().in}>
        <Typography variant="h3" paragraph>
          Permission Denied
        </Typography>
      </m.div>

      <m.div variants={varBounce().in}>
        <Typography sx={{ color: 'text.secondary' }}>
          You don&apos;t have the required permissions to access this page.
        </Typography>
      </m.div>

      <m.div variants={varBounce().in}>
        <Suspense fallback={<Box sx={{ height: 260, my: { xs: 5, sm: 10 } }} />}>
          <ForbiddenIllustration sx={{ height: 260, my: { xs: 5, sm: 10 } }} />
        </Suspense>
      </m.div>

      <Button
        variant="contained"
        color="primary"
        onClick={() => window.location.replace('/auth/crossing')}
        sx={{ mb: 3 }}
      >
        Go Home
      </Button>

      <m.div variants={varBounce().in}>
        <Typography sx={{ color: 'text.secondary' }}>
          Think this is a mistake?{' '}
          <Link href="https://deskos.zohodesk.in/portal/en/newticket/" target="_blank" rel="noreferrer">
            Visit Support
          </Link>
        </Typography>
      </m.div>
    </Container>
  );
}
